import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {AT_COPILOT_VALUE} from '@github-ui/filter-core/providers'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ActionList, ActionMenu, AvatarStack, Button} from '@primer/react'
import styles from './AssigneeAvatars.module.css'

interface AssigneeAvatar {
  login: string
  avatarUrl: string
  isCopilot?: boolean
  /** Server-provided `assignee:` qualifier value (e.g. `@claude[agent]`, `app/<slug>`); takes precedence when present. */
  filterValue?: string
}

interface AssigneeAvatarsProps {
  assignees: AssigneeAvatar[]
  /**
   * Invoked with the assignee's `assignee:` filter value when an avatar is clicked: `filterValue` when provided,
   * otherwise `@copilot` for Copilot or the login. When omitted the avatars are static.
   */
  onSelect?: (filterValue: string) => void
  /** Class applied to the filter button so each row can preserve its own link styling. */
  filterLinkClassName?: string
  /** Right-align the stack (used in the default-density metadata column). */
  alignRight?: boolean
  /** Limit visible avatars and expose the complete list from an overflow menu. */
  maxVisible?: number
}

function getAssigneeFilterValue(assignee: AssigneeAvatar) {
  if (assignee.filterValue) return assignee.filterValue
  return assignee.isCopilot ? AT_COPILOT_VALUE : assignee.login
}

/**
 * Shared assignee avatar stack for dashboard list rows. Renders user hovercards
 * so issue and pull-request rows behave identically in the mixed list. Returns
 * `null` when there are no assignees.
 */
export function AssigneeAvatars({
  assignees,
  onSelect,
  filterLinkClassName,
  alignRight = false,
  maxVisible,
}: AssigneeAvatarsProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const visibleAssignees = maxVisible === undefined ? assignees : assignees.slice(0, maxVisible)
  const hiddenCount = assignees.length - visibleAssignees.length
  const [firstAssignee] = assignees

  if (!firstAssignee) return null

  const renderAssignee = (assignee: AssigneeAvatar) => {
    const hovercardAttributes = assignee.isCopilot
      ? getHovercardAttributesForType('copilot', {botSlug: assignee.login})
      : getHovercardAttributesForType('user', {login: assignee.login})
    const avatar = assignee.isCopilot ? (
      <CopilotAvatar
        key={assignee.login}
        size="small"
        className={styles.squareAvatar}
        isDecorative={Boolean(onSelect)}
      />
    ) : (
      <GitHubAvatar key={assignee.login} src={assignee.avatarUrl} size={20} alt={onSelect ? '' : assignee.login} />
    )

    return onSelect ? (
      <button
        key={assignee.login}
        type="button"
        className={filterLinkClassName}
        onClick={() => onSelect(getAssigneeFilterValue(assignee))}
        aria-label={`Filter by assignee ${assignee.login}`}
        {...hovercardAttributes}
      >
        {avatar}
      </button>
    ) : assignee.isCopilot ? (
      <span key={assignee.login} {...hovercardAttributes}>
        {avatar}
      </span>
    ) : (
      <GitHubAvatar
        key={assignee.login}
        src={assignee.avatarUrl}
        size={20}
        alt={assignee.login}
        {...hovercardAttributes}
      />
    )
  }

  if (maxVisible === undefined) {
    if (assignees.length === 1) return renderAssignee(firstAssignee)
    return <AvatarStack alignRight={alignRight}>{assignees.map(renderAssignee)}</AvatarStack>
  }

  return (
    <div className={styles.container}>
      <AvatarStack alignRight={alignRight} disableExpand={hiddenCount > 0}>
        {visibleAssignees.map(renderAssignee)}
      </AvatarStack>
      {hiddenCount > 0 && (
        <ActionMenu>
          <ActionMenu.Anchor>
            <Button
              type="button"
              size="small"
              variant="invisible"
              className={styles.overflowButton}
              aria-label={`+${hiddenCount}, show all ${assignees.length} assignees`}
              onClick={event => event.stopPropagation()}
            >
              {`+${hiddenCount}`}
            </Button>
          </ActionMenu.Anchor>
          <ActionMenu.Overlay align="end" onClick={event => event.stopPropagation()}>
            <ActionList>
              <ActionList.Group>
                <ActionList.GroupHeading>{onSelect ? 'Filter by assignee' : 'Assignees'}</ActionList.GroupHeading>
                {assignees.map(assignee => (
                  <ActionList.Item
                    key={assignee.login}
                    onSelect={onSelect ? () => onSelect(getAssigneeFilterValue(assignee)) : undefined}
                    inactiveText={onSelect ? undefined : 'Assignee'}
                  >
                    <ActionList.LeadingVisual>
                      <GitHubAvatar src={assignee.avatarUrl} size={20} alt="" />
                    </ActionList.LeadingVisual>
                    {assignee.login}
                  </ActionList.Item>
                ))}
              </ActionList.Group>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
    </div>
  )
}
